/**
 * Product Card — JavaScript
 *
 * Handles:
 *   1. Wishlist toggle (localStorage‑based, fires a custom event)
 *   2. Quick‑add to cart via Shopify AJAX Cart API
 *   3. Quick‑view (navigates to product page or fires custom event)
 */

(function () {
  'use strict';

  /* ======================================================================
     WISHLIST
     ====================================================================== */

  const WISHLIST_KEY = 'pc_wishlist';

  /**
   * Read wishlist IDs from localStorage.
   * @returns {Set<string>}
   */
  function getWishlist() {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  }

  /**
   * Persist wishlist IDs to localStorage.
   * @param {Set<string>} list
   */
  function saveWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify([...list]));
    } catch {
      /* storage full — silent fail */
    }
  }

  /**
   * Sync every wishlist button on the page with localStorage state.
   */
  function syncWishlistUI() {
    const list = getWishlist();
    document.querySelectorAll('.product-card__wishlist').forEach(function (btn) {
      const id = btn.dataset.productId;
      if (!id) return;
      btn.classList.toggle('is-active', list.has(id));
      btn.setAttribute(
        'aria-label',
        list.has(id) ? 'Remove from wishlist' : 'Add to wishlist'
      );
    });
  }

  /**
   * Handle wishlist button click.
   * @param {Event} e
   */
  function handleWishlistClick(e) {
    const btn = e.currentTarget;
    const id = btn.dataset.productId;
    if (!id) return;

    const list = getWishlist();

    if (list.has(id)) {
      list.delete(id);
    } else {
      list.add(id);
    }

    saveWishlist(list);
    syncWishlistUI();

    // Fire a custom event so other parts of the theme can react
    document.dispatchEvent(
      new CustomEvent('product-card:wishlist-change', {
        detail: { productId: id, wishlisted: list.has(id) },
      })
    );
  }

  /* ======================================================================
     QUICK ADD TO CART
     ====================================================================== */

  /**
   * Add an item to the cart via Shopify AJAX API.
   * @param {HTMLButtonElement} btn
   */
  async function handleQuickAdd(btn) {
    if (btn.classList.contains('is-loading') || btn.classList.contains('is-added')) return;

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    const label = btn.querySelector('.pc-quick-add-label');
    const originalText = label ? label.textContent : '';

    btn.classList.add('is-loading');
    btn.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(function () {
          return {};
        });
        throw new Error(errData.description || 'Could not add to cart');
      }

      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      if (label) label.textContent = '✓';

      // Fire a custom event so the cart drawer / counter can update
      document.dispatchEvent(
        new CustomEvent('product-card:cart-add', {
          detail: { variantId: variantId },
        })
      );

      // Refresh cart count in the header (common pattern)
      refreshCartCount();

      setTimeout(function () {
        btn.classList.remove('is-added');
        btn.disabled = false;
        if (label) label.textContent = originalText;
      }, 1800);
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.disabled = false;
      if (label) {
        label.textContent = err.message || 'Error';
        setTimeout(function () {
          label.textContent = originalText;
        }, 2000);
      }
    }
  }

  /**
   * Optionally refresh cart item count badge in the header.
   */
  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const cart = await res.json();
      // Update any element with [data-cart-count]
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = cart.item_count;
      });
    } catch {
      /* silent */
    }
  }

  /* ======================================================================
     QUICK VIEW
     ====================================================================== */

  /**
   * Handle quick-view button click.
   * Fires a custom event that a quick-view modal component can listen to,
   * or falls back to navigating to the product page.
   * @param {HTMLButtonElement} btn
   */
  function handleQuickView(btn) {
    const handle = btn.dataset.productHandle;
    if (!handle) return;

    // Fire a custom event — a quick-view modal can intercept this
    const event = new CustomEvent('product-card:quick-view', {
      detail: { productHandle: handle },
      cancelable: true,
    });

    const handled = !document.dispatchEvent(event);

    // If no listener prevented default, navigate to the product page
    if (!handled) {
      window.location.href = '/products/' + handle;
    }
  }

  /* ======================================================================
     INIT — uses event delegation for dynamic content
     ====================================================================== */

  function init() {
    syncWishlistUI();

    document.addEventListener('click', function (e) {
      // Wishlist
      var wishBtn = e.target.closest('.product-card__wishlist');
      if (wishBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleWishlistClick(e);
        return;
      }

      // Quick‑view (eye icon)
      var qvBtn = e.target.closest('.product-card__quick-view');
      if (qvBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleQuickView(qvBtn);
        return;
      }

      // Quick‑add (legacy full-width button, if still used)
      var qaBtn = e.target.closest('.product-card__quick-add');
      if (qaBtn) {
        e.preventDefault();
        e.stopPropagation();
        handleQuickAdd(qaBtn);
        return;
      }
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
